// Select with change selected value when value is changed

"use client";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useId, useRef, useState } from "react";
import { FieldValues, Path, UseFormRegister } from "react-hook-form";

import { ALL_CHOICE } from "constants/common";
import { useDetectHalfUp } from "hooks/useDetectHalfUp";
import { cn } from "utils/cn";

export type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

interface SelectProps<T extends FieldValues> {
  options: Option[];
  value?: string;
  label?: string;
  onChange: (_: Option) => void;
  allChoice?: boolean;
  width?: string;
  initialValue?: string;
  selectClassName?: string;
  popDownClass?: string;
  labelClassName?: string;
  listClassName?: string;
  selectedValueClassName?: string;
  errorMode?: boolean;
  errorMessage?: string;
  register?: UseFormRegister<T>;
  required?: boolean;
  registerName?: Path<T>;
  disabled?: boolean;
  ariaLabel?: string;
  isHasTextAfterValue?: boolean;
}

const Select = <T extends FieldValues>({
  options,
  width = "w-[200px]",
  allChoice = false,
  onChange,
  label,
  initialValue = "すべて",
  selectClassName = "",
  popDownClass = "",
  labelClassName = "",
  listClassName = "",
  selectedValueClassName = "",
  errorMode,
  errorMessage,
  register,
  required = false,
  registerName,
  disabled = false,
  ariaLabel,
  value,
  isHasTextAfterValue = false,
}: SelectProps<T>) => {
  const [isOpened, setIsOpened] = useState(false);
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [isError, setIsError] = useState(false);

  const isSameInitialValue = options.filter((option) => option.label === initialValue).length === 0;
  const closePopDown = () => {
    setIsOpened(false);
    // Don't trigger validation when component is disabled
    if (!disabled) {
      setIsError(selectedValue === initialValue && isSameInitialValue);
    }
  };

  const onChangeValue = (value: Option) => {
    setSelectedValue(value.label);
    onChange(value);
    closePopDown();
  };

  // Add logic to change selected value when value is changed
  useEffect(() => {
    if (value) {
      setSelectedValue(options.find((option) => option.value === value)?.label || initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options, initialValue]);

  const optionArray = allChoice ? [{ ...ALL_CHOICE }, ...options] : options;

  const ref = useRef<HTMLDivElement>(null);
  const { isHalfUp } = useDetectHalfUp(ref);
  const labelId = useId();
  const listboxId = useId();

  return (
    <div className={`relative flex flex-col ${label && "gap-1"} ${selectClassName}`} ref={ref}>
      {register && registerName && (
        <input
          {...register(registerName, {
            required,
          })}
          hidden
          value={selectedValue}
        />
      )}
      {label && <p className={`text-14px ${labelClassName}`}>{label}</p>}
      <div
        className={`${width} relative text-14px ${popDownClass}`}
        onBlur={disabled ? undefined : closePopDown}
        tabIndex={disabled ? -1 : 0}
        role="menu"
      >
        <button
          disabled={disabled}
          type="button"
          onClick={() => setIsOpened(true)}
          className={`${
            disabled ? "bg-disabled" : "bg-input-normal"
          } start flex h-12 w-full items-center justify-between rounded border border-input-normal  px-4  text-black focus:border-link focus:shadow-input focus:outline-none`}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpened}
          aria-controls={listboxId}
          aria-labelledby={labelId}
          aria-activedescendant={
            isOpened ? `${listboxId}-${optionArray.findIndex((option) => option.label === selectedValue)}` : undefined
          }
        >
          <p className={`flex flex-1 items-center text-left ${selectedValueClassName}`}>
            <span className={clsx("truncate", isHasTextAfterValue ? "max-w-[170px]" : "max-w-unset w-0 flex-1")}>
              {selectedValue}
            </span>
            {isHasTextAfterValue && (
              <span className="whitespace-nowrap pl-4 text-xs leading-3 text-gray">名義人以外が押印する</span>
            )}
          </p>
          <div className={`relative -right-1 top-[1px] ${!isHalfUp && isOpened && "rotate-180"}`}>
            <FontAwesomeIcon icon={faChevronDown} className="text-base text-gray" />
          </div>
        </button>
        {errorMode && isError && errorMessage && (
          <div className="relative mt-2 text-xs font-bold text-attention" role="alert" aria-label={ariaLabel}>
            {errorMessage}
          </div>
        )}
        <div className={isOpened ? `block w-full` : "hidden"}>
          <ul
            role="listbox"
            aria-labelledby={labelId}
            onPointerDown={(event: React.PointerEvent<HTMLUListElement>) => {
              if (String(event.target).includes("HTMLUListElement")) {
                // Does not trigger onBlur={closePopDown} when clicking/ scrolling on the scrollbar
                event.preventDefault();
              }
            }}
            tabIndex={0}
            className={cn(
              "hide-scrollbar absolute z-10 mt-1 flex max-h-[400px] w-full flex-col overflow-auto rounded border-[1px] border-input-normal bg-white py-1 shadow-md",
              isHalfUp ? "top-12" : "bottom-14",
              listClassName
            )}
          >
            {optionArray.map((option, index) => (
              <li
                key={index}
                role="option"
                aria-selected={selectedValue !== option.label}
                id={`${listboxId}-${option.value}`}
              >
                <button
                  onMouseDown={() => onChangeValue(option)}
                  tabIndex={-1}
                  className={clsx(
                    "w-full px-4 py-1 text-left ",
                    option.disabled
                      ? "cursor-default text-disabled"
                      : "cursor-pointer hover:bg-primary hover:text-white"
                  )}
                  type="button"
                  disabled={option.disabled}
                  aria-label={option.label}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Select;
